import { Table } from '@tanstack/react-table';
import { CompanyCustomBenefitAssignmentEmployee } from '@pxtra-client-lib-legacy/gen';
import { TableFilter } from '../shared/filter';
import {
  TableRoofChipList,
  TableRoofFilter
} from '../../compound/tables/presentation/table/roofs/shared';
import { DataTableResults } from '../shared/results';
import { DataTableSearch } from '../shared/search';
import { IconButton } from '@mui/material';
import { BudgetIcon } from '../../../icons';
import { useTranslation } from 'react-i18next';
import { TooltipComponent } from '../../atomic/feedback';

interface CustomBenefitToolBarProps {
  table: Table<CompanyCustomBenefitAssignmentEmployee>;
  benefitName: string;
  onBulkAssign: (employeeIds: string[]) => void;
}

export const CustomBenefitToolBar = ({
  table,
  benefitName,
  onBulkAssign
}: CustomBenefitToolBarProps) => {
  const { t } = useTranslation();
  const selectedRows = table.getSelectedRowModel().rows;
  const hasSelection = selectedRows.length > 0;

  return (
    <div className="flex items-center min-h-12 justify-between py-2">
      <DataTableResults table={table} />
      <TableRoofChipList
        chips={table.getState().columnFilters.map((filter) => {
          const column = table.getColumn(filter.id);
          const filterOptions = column?.columnDef.meta?.filterOptions;

          let label;
          if (Array.isArray(filter.value) && filter.id === 'entry_date') {
            const [min, max] = filter.value;
            if (min && max) {
              label = `${min} - ${max}`;
            } else if (min) {
              label = `≥ ${min}`;
            } else if (max) {
              label = `≤ ${max}`;
            } else {
              label = '';
            }
          } else {
            label = String(filter.value);
          }

          if (filterOptions) {
            const option = filterOptions.find((opt) => String(opt.value) === String(filter.value));
            if (option) {
              label = option.text;
            }
          }

          return {
            label,
            value: filter.id,
            onDelete: () => {
              table.getColumn(filter.id)?.setFilterValue(undefined);
              table.resetRowSelection();
            }
          };
        })}
        justify="flex-start"
        sx={{ width: '500px' }}
      />
      {hasSelection ? (
        <div className="flex items-center gap-2">
          <TooltipComponent
            title={t('custom:assign_individual_budget_custom', {
              defaultValue: '',
              customBenefit: benefitName
            })}
            placement="bottom"
            arrow
          >
            <IconButton
              onClick={() =>
                onBulkAssign(selectedRows.map((row) => row.original.id).filter(Boolean) as string[])
              }
              size="small"
              sx={{ color: 'primary.main' }}
            >
              <BudgetIcon color="primary.main" />
            </IconButton>
          </TooltipComponent>
        </div>
      ) : (
        <>
          <DataTableSearch table={table} />
          <TableRoofFilter renderFilter={() => <TableFilter table={table} />} />
        </>
      )}
    </div>
  );
};
